import { index, modelOptions, mongoose, PropType, post, prop, Ref } from "@typegoose/typegoose";
import { z } from "zod";
import { ENTITY_NAME_MAX_LENGTH, entityNameZodValidator, ObjectIdString } from "../../validators/index.js";
import { getZodSchemaFromClass, validate } from "../../zod-decorator/index.js";
import type { Organization } from "../organization/organization.js";
import { ScdDocument } from "../scdDocument/scdDocument.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import type {
    NotificationSubscriptionFieldsWithDefaults,
    NotificationSubscriptionSchemaTypes,
} from "../types/notificationSubscription.d.js";
import { type DtoValidatorTypes } from "../validatableClass.js";
import { NotificationSubscriptionRule } from "./subSchemas/notificationSubscriptionRule.js";

const SCHEMA_KEY = "notificationSubscriptions" as const;

/** How loudly to deliver a warning type: `standard` honors quiet hours, `critical` may pierce them. */
export type NotificationMode = "standard" | "critical";

/**
 * An organization's routing rules: which warnings (by scope and type) reach its members, and how
 * loudly. The embedded rules are the source of truth and are edited as a whole (a client edit
 * rewrites the full rule set as one new version). SCD Type-2, so edits keep a versioned coverage
 * history.
 */
@index({ owner: 1, name: 1 }, { unique: true, partialFilterExpression: { versionIsCurrent: true, deleted: false } })
// The warning hot path: finds current subscriptions relevant to a warning by its scope keys
// (org, AOI) — multikey over the embedded rules' scopes, so the set of relevant scopes can never
// drift from the rules themselves. Scope-kind and warning-type matching happen in memory afterward.
@index({ "rules.scopes.targetId": 1 }, { partialFilterExpression: { versionIsCurrent: true, deleted: false } })
@modelOptions({
    schemaOptions: {
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        collection: "notificationSubscriptions" as const satisfies typeof SCHEMA_KEY,
        _id: true,
    },
})
@post<SchemaBase<NotificationSubscription>>(...addSchemaKeyHook(SCHEMA_KEY))
export class NotificationSubscription extends ScdDocument<
    NotificationSubscription,
    NotificationSubscriptionFieldsWithDefaults
> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    public _id!: mongoose.Types.ObjectId;

    /**
     * The owning organization. Taken from the authenticated caller on write, never the request body,
     * so it is response-only; every rule's scope must belong to it (enforced at the write boundary).
     */
    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    @prop({ type: () => mongoose.Types.ObjectId, required: true, ref: "Organization" })
    public owner!: Ref<Organization>;

    /** Human-readable name, unique per owner. */
    @validate({ zodValidator: entityNameZodValidator })
    @prop({ type: () => String, required: true, maxlength: ENTITY_NAME_MAX_LENGTH })
    public name!: string;

    /** The routing atoms, OR-matched: a warning is covered if any rule matches its scope and type (at least one rule). */
    // requiredOnUpdate: PUT replaces the whole rule set, so an update must always carry it.
    @validate({
        childClass: NotificationSubscriptionRule,
        childClassTransform: (object) => z.array(object).min(1),
        requiredOnUpdate: true,
    })
    @prop({ type: () => [NotificationSubscriptionRule], required: true, _id: false }, PropType.ARRAY)
    public rules!: NotificationSubscriptionRule[];

    /** Whether to send an all-clear when a covered warning resolves. */
    @validate({ zodValidator: z.boolean(), hasDefault: true })
    @prop({ type: () => Boolean, required: true, default: false })
    public sendAllClears: boolean = false;

    static getters = {} as const satisfies Record<string, (self: NotificationSubscription) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<NotificationSubscriptionSchemaTypes>;

    constructor(
        properties?: SchemaConstructorArgs<NotificationSubscription, NotificationSubscriptionFieldsWithDefaults>,
    ) {
        super(properties);
        Object.assign(this, properties);
    }
}

NotificationSubscription.dtoValidators = getZodSchemaFromClass(
    NotificationSubscription,
) as unknown as DtoValidatorTypes<NotificationSubscriptionSchemaTypes>;
